import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { t } from './data/translations';
import Sidebar from './components/Sidebar';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import CodeBlock, { CodeTabs } from './components/CodeBlock';
import { 
  paymentInitializedExamples,
  providerRequestedExamples,
  providerSuccessExamples,
  providerFailedExamples,
  orderCreatedExamples,
  revenueRecordedExamples,
  batchEventsExamples,
  sdkExamplesNodeJS,
  sdkExamplesPHP
} from './data/codeExamples';
import { 
  ArrowRight, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  ExternalLink,
  Shield,
  Zap,
  Clock,
  Server,
  Globe,
  Lock
} from 'lucide-react';
import './App.css';

function AppContent() {
  const [activeSection, setActiveSection] = useState('what-is');
  const contentRef = useRef(null);
  const { language } = useLanguage();

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let currentSection = 'quick-start';
      
      sections.forEach(section => {
        const rect = section.getBoundingClientRect();
        if (rect.top <= 100) {
          currentSection = section.id;
        }
      });
      
      setActiveSection(currentSection);
    };

    const content = contentRef.current;
    if (content) {
      content.addEventListener('scroll', handleScroll);
      return () => content.removeEventListener('scroll', handleScroll);
    }
  }, []);

  return (
    <div className="app">
      <Sidebar activeSection={activeSection} onSectionChange={scrollToSection} />
      
      <main className="main-content" ref={contentRef}>
        <header className="header">
          <div className="header-content">
            <div className="header-title">
              <h1>{t('headerTitle', language)}</h1>
              <div className="header-badges">
                <span className="badge badge-version">v1.2</span>
                <span className="badge badge-updated">January 2026</span>
              </div>
            </div>
            <div className="header-controls">
              <LanguageToggle />
              <ThemeToggle />
            </div>
          </div>
        </header>

        <div className="content">
          {/* What is SilentLoss Section */}
          <section id="what-is" className="section">
            <h2>{t('whatIsTitle', language)}</h2>
            <p className="section-intro">
              {t('whatIsDesc', language)}
            </p>

            <div className="problem-box">
              <div className="problem-header">{t('problemTitle', language)}</div>
              <div className="problem-content">
                <p className="problem-flow">{t('problemDesc1', language)}</p>
                <div className="problem-reasons">
                  <strong>{t('problemWhy', language)}</strong>
                  <ul>
                    <li>{t('problemReason1', language)}</li>
                    <li>{t('problemReason2', language)}</li>
                    <li>{t('problemReason3', language)}</li>
                    <li>{t('problemReason4', language)}</li>
                    <li>{t('problemReason5', language)}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="solution-box">
              <p>{t('solutionDesc', language)}</p>
            </div>
          </section>

          {/* Fire & Forget Section */}
          <section id="fire-forget" className="section">
            <h2>{t('fireForgetTitle', language)}</h2>
            
            <div className="warning-box critical">
              <AlertTriangle size={20} />
              <div>
                <strong>{t('critical', language)}:</strong> {t('fireForgetCritical', language)}
              </div>
            </div>

            <h3>{t('fireForgetWhy', language)}</h3>

            <div className="comparison-grid">
              <div className="comparison-card wrong">
                <h4>{t('wrongSyncTitle', language)}</h4>
                <div className="comparison-flow">
                  <p>{t('wrongSyncDesc1', language)}</p>
                  <p className="highlight">{t('wrongSyncDesc2', language)}</p>
                </div>
                <ul className="problem-list">
                  <li>{t('wrongSyncProblem1', language)}</li>
                  <li>{t('wrongSyncProblem2', language)}</li>
                  <li>{t('wrongSyncProblem3', language)}</li>
                </ul>
              </div>

              <div className="comparison-card correct">
                <h4>{t('rightAsyncTitle', language)}</h4>
                <div className="comparison-flow">
                  <p>{t('rightAsyncDesc1', language)}</p>
                  <p className="highlight">{t('rightAsyncDesc2', language)}</p>
                  <p>{t('rightAsyncDesc3', language)}</p>
                </div>
                <ul className="benefit-list">
                  <li>{t('rightAsyncBenefit1', language)}</li>
                  <li>{t('rightAsyncBenefit2', language)}</li>
                  <li>{t('rightAsyncBenefit3', language)}</li>
                  <li>{t('rightAsyncBenefit4', language)}</li>
                </ul>
              </div>
            </div>

            <h3>{t('basicRulesTitle', language)}</h3>
            <div className="rules-grid">
              <div className="rule-card">
                <div className="rule-icon">🚀</div>
                <h4>{t('ruleAsync', language)}</h4>
                <p>{t('ruleAsyncDesc', language)}</p>
              </div>
              <div className="rule-card">
                <div className="rule-icon">🛡️</div>
                <h4>{t('ruleSwallow', language)}</h4>
                <p>{t('ruleSwallowDesc', language)}</p>
              </div>
              <div className="rule-card">
                <div className="rule-icon">🔄</div>
                <h4>{t('ruleBackgroundRetry', language)}</h4>
                <p>{t('ruleBackgroundRetryDesc', language)}</p>
              </div>
              <div className="rule-card">
                <div className="rule-icon">📝</div>
                <h4>{t('ruleLogging', language)}</h4>
                <p>{t('ruleLoggingDesc', language)}</p>
              </div>
            </div>
          </section>

          {/* Quick Start Section */}
          <section id="quick-start" className="section">
            <h2>{t('quickStartTitle', language)}</h2>
            <p className="section-intro">
              {t('quickStartIntro', language)}
            </p>

            <div className="feature-grid">
              <div className="feature-card">
                <div className="feature-icon">
                  <Zap size={24} />
                </div>
                <h3>{t('featureFastTitle', language)}</h3>
                <p>{t('featureFastDesc', language)}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <Shield size={24} />
                </div>
                <h3>{t('featureSecureTitle', language)}</h3>
                <p>{t('featureSecureDesc', language)}</p>
              </div>
              <div className="feature-card">
                <div className="feature-icon">
                  <Clock size={24} />
                </div>
                <h3>{t('featureRealtimeTitle', language)}</h3>
                <p>{t('featureRealtimeDesc', language)}</p>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>{t('step1Title', language)}</h3>
                <p>{t('step1Desc', language)}</p>
                <div className="step-path">
                  Dashboard <ArrowRight size={16} /> Settings <ArrowRight size={16} /> API Keys <ArrowRight size={16} /> Create New Key
                </div>
                <div className="info-box">
                  <Info size={18} />
                  <div>
                    <strong>{t('apiKeyFormat', language)}</strong>
                    <code>sl_abc123def456ghi789jkl012mno345pqr</code>
                  </div>
                </div>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>{t('step2Title', language)}</h3>
                <CodeTabs examples={paymentInitializedExamples} />
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>{t('step3Title', language)}</h3>
                <p>{t('step3Desc', language)}</p>
                <div className="flow-timeline">
                  <div className="flow-step">
                    <div className="flow-step-indicator">
                      <span className="flow-step-number">1</span>
                      <div className="flow-step-line"></div>
                    </div>
                    <div className="flow-step-content">
                      <span className="flow-step-title">Payment Initialized</span>
                      <code>/events/payment/initialized</code>
                    </div>
                  </div>
                  <div className="flow-step">
                    <div className="flow-step-indicator">
                      <span className="flow-step-number">2</span>
                      <div className="flow-step-line"></div>
                    </div>
                    <div className="flow-step-content">
                      <span className="flow-step-title">Provider Requested</span>
                      <code>/events/payment/provider-requested</code>
                    </div>
                  </div>
                  <div className="flow-step">
                    <div className="flow-step-indicator">
                      <span className="flow-step-number">3</span>
                      <div className="flow-step-line"></div>
                    </div>
                    <div className="flow-step-content">
                      <span className="flow-step-title">Provider Success</span>
                      <code>/events/payment/provider-success</code>
                    </div>
                  </div>
                  <div className="flow-step">
                    <div className="flow-step-indicator">
                      <span className="flow-step-number">4</span>
                      <div className="flow-step-line"></div>
                    </div>
                    <div className="flow-step-content">
                      <span className="flow-step-title">Order Created</span>
                      <code>/events/payment/order-created</code>
                    </div>
                  </div>
                  <div className="flow-step last">
                    <div className="flow-step-indicator">
                      <span className="flow-step-number">5</span>
                    </div>
                    <div className="flow-step-content">
                      <span className="flow-step-title">Revenue Recorded</span>
                      <code>/events/payment/revenue-recorded</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Authentication Section */}
          <section id="authentication" className="section">
            <h2>{t('authTitle', language)}</h2>
            
            <h3>{t('authUsageTitle', language)}</h3>
            <p>{t('authUsageDesc', language)}</p>
            <CodeBlock 
              code={`Authorization: Bearer sl_abc123def456ghi789jkl012mno345pqr`}
              language="bash"
            />

            <h3>{t('authTypesTitle', language)}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('tableEnvironment', language)}</th>
                    <th>{t('tablePrefix', language)}</th>
                    <th>{t('tableRateLimit', language)}</th>
                    <th>{t('tableUsage', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="badge badge-success">{t('tableProduction', language)}</span></td>
                    <td><code>sl_</code></td>
                    <td>1000/min</td>
                    <td>{t('tableProdEnv', language)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{t('authIpWhitelistTitle', language)}</h3>
            <p>{t('authIpWhitelistDesc', language)}</p>
            <div className="step-path">
              Dashboard <ArrowRight size={16} /> Settings <ArrowRight size={16} /> API Keys <ArrowRight size={16} /> Edit <ArrowRight size={16} /> IP Whitelist
            </div>
          </section>

          {/* Event Types Section */}
          <section id="event-types" className="section">
            <h2>{t('eventTypesTitle', language)}</h2>
            <p>{t('eventTypesDesc', language)}</p>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('tableEvent', language)}</th>
                    <th>{t('tableEndpoint', language)}</th>
                    <th>{t('tableWhenSent', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Initialized</strong></td>
                    <td><code>/events/payment/initialized</code></td>
                    <td>{t('eventInitializedWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Provider Requested</strong></td>
                    <td><code>/events/payment/provider-requested</code></td>
                    <td>{t('eventProviderRequestedWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Provider Success</strong></td>
                    <td><code>/events/payment/provider-success</code></td>
                    <td>{t('eventProviderSuccessWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Provider Failed</strong></td>
                    <td><code>/events/payment/provider-failed</code></td>
                    <td>{t('eventProviderFailedWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Order Created</strong></td>
                    <td><code>/events/payment/order-created</code></td>
                    <td>{t('eventOrderCreatedWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Revenue Recorded</strong></td>
                    <td><code>/events/payment/revenue-recorded</code></td>
                    <td>{t('eventRevenueRecordedWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Refund Initiated</strong></td>
                    <td><code>/events/payment/refund-initiated</code></td>
                    <td>{t('eventRefundInitiatedWhen', language)}</td>
                  </tr>
                  <tr>
                    <td><strong>Refund Completed</strong></td>
                    <td><code>/events/payment/refund-completed</code></td>
                    <td>{t('eventRefundCompletedWhen', language)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Endpoints Section */}
          <section id="endpoints" className="section">
            <h2>{t('endpointsTitle', language)}</h2>
            
            <h3>{t('baseUrlTitle', language)}</h3>
            <div className="endpoint-cards">
              <div className="endpoint-card">
                <div className="endpoint-header">
                  <Server size={18} />
                  <span>{t('tableProduction', language)}</span>
                </div>
                <code>https://api.silentloss.io/api/v1</code>
              </div>
            </div>

            <h3>{t('eventEndpointsTitle', language)}</h3>
            <div className="endpoints-list">
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/initialized</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/provider-requested</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/provider-success</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/provider-failed</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/order-created</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/revenue-recorded</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/refund-initiated</code>
              </div>
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/payment/refund-completed</code>
              </div>
            </div>

            <h3>{t('batchEndpointTitle', language)}</h3>
            <div className="endpoints-list">
              <div className="endpoint-item">
                <span className="method post">POST</span>
                <code>/api/v1/events/batch</code>
              </div>
            </div>
          </section>

          {/* JSON Formats Section */}
          <section id="json-formats" className="section">
            <h2>{t('jsonFormatsTitle', language)}</h2>
            <p>{t('jsonFormatsDesc', language)}</p>
          </section>

          {/* Payment Initialized */}
          <section id="payment-initialized" className="section">
            <h3>{t('paymentInitializedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/initialized</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('paymentInitializedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "amount": 100.00,
  "currency": "USD",
  "provider": "stripe",
  "customer_email": "customer@example.com"
}`}
              language="json"
            />

            <h4>Field Descriptions</h4>
            <div className="table-container">
              <table className="data-table field-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Type</th>
                    <th>Required</th>
                    <th>Description</th>
                    <th>Example</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>payment_intent_id</code></td>
                    <td>string</td>
                    <td><CheckCircle size={16} className="icon-success" /></td>
                    <td>{t('uniquePaymentId', language)}</td>
                    <td><code>"order_12345"</code></td>
                  </tr>
                  <tr>
                    <td><code>amount</code></td>
                    <td>decimal</td>
                    <td><CheckCircle size={16} className="icon-success" /></td>
                    <td>{t('paymentAmount', language)}</td>
                    <td><code>100.00</code></td>
                  </tr>
                  <tr>
                    <td><code>currency</code></td>
                    <td>string</td>
                    <td><CheckCircle size={16} className="icon-success" /></td>
                    <td>{t('currencyIso', language)}</td>
                    <td><code>"USD"</code>, <code>"EUR"</code>, <code>"TRY"</code></td>
                  </tr>
                  <tr>
                    <td><code>provider</code></td>
                    <td>string</td>
                    <td><CheckCircle size={16} className="icon-success" /></td>
                    <td>{t('paymentProvider', language)}</td>
                    <td><code>"stripe"</code>, <code>"iyzico"</code></td>
                  </tr>
                  <tr>
                    <td><code>customer_email</code></td>
                    <td>string</td>
                    <td><CheckCircle size={16} className="icon-success" /></td>
                    <td>{t('customerEmail', language)}</td>
                    <td><code>"customer@example.com"</code></td>
                  </tr>
                  <tr>
                    <td><code>customer_id</code></td>
                    <td>string</td>
                    <td><span className="optional">{t('optional', language)}</span></td>
                    <td>{t('customerId', language)}</td>
                    <td><code>"cus_abc123"</code></td>
                  </tr>
                  <tr>
                    <td><code>product_id</code></td>
                    <td>string</td>
                    <td><span className="optional">{t('optional', language)}</span></td>
                    <td>{t('productServiceId', language)}</td>
                    <td><code>"prod_xyz789"</code></td>
                  </tr>
                  <tr>
                    <td><code>product_name</code></td>
                    <td>string</td>
                    <td><span className="optional">{t('optional', language)}</span></td>
                    <td>{t('productServiceName', language)}</td>
                    <td><code>"Premium Plan"</code></td>
                  </tr>
                  <tr>
                    <td><code>metadata</code></td>
                    <td>object</td>
                    <td><span className="optional">{t('optional', language)}</span></td>
                    <td>{t('additionalInfo', language)}</td>
                    <td><code>{`{key: value}`}</code></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h4>{t('exampleRequest', language)}</h4>
            <CodeTabs examples={paymentInitializedExamples} />

            <h4>{t('successResponse', language)}</h4>
            <CodeBlock 
              code={`{
  "success": true,
  "message": "Payment initialized event recorded",
  "data": {
    "payment_intent_id": "order_12345",
    "status": "initialized",
    "timestamp": "2026-01-13T10:30:00Z"
  }
}`}
              language="json"
            />
          </section>

          {/* Provider Requested */}
          <section id="provider-requested" className="section">
            <h3>{t('providerRequestedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/provider-requested</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('providerRequestedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "provider": "stripe",
  "provider_request_id": "pi_3ABC123DEF456",
  "amount": 100.00,
  "currency": "USD"
}`}
              language="json"
            />

            <h4>{t('exampleRequest', language)}</h4>
            <CodeTabs examples={providerRequestedExamples} />
          </section>

          {/* Provider Success */}
          <section id="provider-success" className="section">
            <h3>{t('providerSuccessTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/provider-success</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('providerSuccessWhen', language)}
            </p>

            <div className="warning-box">
              <AlertTriangle size={20} />
              <div>
                <strong>{t('important', language)}:</strong> {t('providerSuccessWarning', language)}
              </div>
            </div>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "provider": "stripe",
  "provider_payment_id": "pi_3ABC123DEF456",
  "provider_transaction_id": "ch_3ABC123DEF456",
  "amount": 100.00,
  "currency": "USD",
  "provider_status": "succeeded"
}`}
              language="json"
            />

            <h4>{t('exampleRequest', language)}</h4>
            <CodeTabs examples={providerSuccessExamples} />
          </section>

          {/* Provider Failed */}
          <section id="provider-failed" className="section">
            <h3>{t('providerFailedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/provider-failed</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('providerFailedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "provider": "stripe",
  "error_code": "card_declined",
  "error_message": "Your card was declined"
}`}
              language="json"
            />

            <h4>{t('exampleRequest', language)}</h4>
            <CodeTabs examples={providerFailedExamples} />
          </section>

          {/* Order Created */}
          <section id="order-created" className="section">
            <h3>{t('orderCreatedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/order-created</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('orderCreatedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "order_id": "ORD-2026-00123",
  "amount": 100.00,
  "currency": "USD"
}`}
              language="json"
            />

            <h4>{t('exampleRequest', language)}</h4>
            <CodeTabs examples={orderCreatedExamples} />
          </section>

          {/* Revenue Recorded */}
          <section id="revenue-recorded" className="section">
            <h3>{t('revenueRecordedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/revenue-recorded</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('revenueRecordedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "revenue_amount": 100.00,
  "currency": "USD",
  "recorded_at": "2026-01-13T10:35:00Z"
}`}
              language="json"
            />

            <h4>{t('exampleRequest', language)}</h4>
            <CodeTabs examples={revenueRecordedExamples} />
          </section>

          {/* Refund Initiated */}
          <section id="refund-initiated" className="section">
            <h3>{t('refundInitiatedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/refund-initiated</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('refundInitiatedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "refund_amount": 100.00,
  "currency": "USD",
  "reason": "customer_request"
}`}
              language="json"
            />
          </section>

          {/* Refund Completed */}
          <section id="refund-completed" className="section">
            <h3>{t('refundCompletedTitle', language)}</h3>
            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/payment/refund-completed</code>
            </div>
            <p className="event-description">
              <strong>{t('whenToSend', language)}</strong> {t('refundCompletedWhen', language)}
            </p>

            <h4>{t('requiredFields', language)}</h4>
            <CodeBlock 
              code={`{
  "payment_intent_id": "order_12345",
  "refund_amount": 100.00,
  "currency": "USD",
  "refund_status": "succeeded"
}`}
              language="json"
            />
          </section>

          {/* Batch Events */}
          <section id="batch-events" className="section">
            <h2>{t('batchEventsTitle', language)}</h2>
            <p>{t('batchEventsDesc', language)}</p>

            <div className="endpoint-badge">
              <span className="method post">POST</span>
              <code>/api/v1/events/batch</code>
            </div>

            <h3>{t('batchEventTypesMapping', language)}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>{t('correspondingEndpoint', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><code>payment.initialized</code></td><td><code>/events/payment/initialized</code></td></tr>
                  <tr><td><code>payment.provider_requested</code></td><td><code>/events/payment/provider-requested</code></td></tr>
                  <tr><td><code>payment.provider_success</code></td><td><code>/events/payment/provider-success</code></td></tr>
                  <tr><td><code>payment.provider_failed</code></td><td><code>/events/payment/provider-failed</code></td></tr>
                  <tr><td><code>payment.order_created</code></td><td><code>/events/payment/order-created</code></td></tr>
                  <tr><td><code>payment.revenue_recorded</code></td><td><code>/events/payment/revenue-recorded</code></td></tr>
                  <tr><td><code>payment.refund_initiated</code></td><td><code>/events/payment/refund-initiated</code></td></tr>
                  <tr><td><code>payment.refund_completed</code></td><td><code>/events/payment/refund-completed</code></td></tr>
                </tbody>
              </table>
            </div>

            <div className="info-box">
              <Info size={18} />
              <div>
                <strong>{t('batchLimitsTitle', language)}</strong>
                <ul>
                  <li>{t('batchLimit1', language)}</li>
                  <li>{t('batchLimit2', language)}</li>
                </ul>
              </div>
            </div>

            <h3>{t('exampleRequest', language)}</h3>
            <CodeTabs examples={batchEventsExamples} />

            <h3>{t('successResponse', language)}</h3>
            <CodeBlock 
              code={`{
  "success": true,
  "message": "Batch events processed",
  "data": {
    "total": 2,
    "successful": 2,
    "failed": 0,
    "results": [
      {
        "index": 0,
        "payment_intent_id": "order_12345",
        "status": "success"
      },
      {
        "index": 1,
        "payment_intent_id": "order_12345",
        "status": "success"
      }
    ]
  }
}`}
              language="json"
            />
          </section>

          {/* Error Handling */}
          <section id="error-handling" className="section">
            <h2>{t('errorHandlingTitle', language)}</h2>

            <h3>{t('httpStatusCodesTitle', language)}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('tableCode', language)}</th>
                    <th>{t('tableMeaning', language)}</th>
                    <th>{t('tableAction', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="status-code success">200</span></td>
                    <td>{t('statusSuccess', language)}</td>
                    <td>{t('statusSuccessAction', language)}</td>
                  </tr>
                  <tr>
                    <td><span className="status-code warning">400</span></td>
                    <td>{t('statusBadRequest', language)}</td>
                    <td>{t('statusBadRequestAction', language)}</td>
                  </tr>
                  <tr>
                    <td><span className="status-code error">401</span></td>
                    <td>{t('statusUnauthorized', language)}</td>
                    <td>{t('statusUnauthorizedAction', language)}</td>
                  </tr>
                  <tr>
                    <td><span className="status-code warning">422</span></td>
                    <td>{t('statusValidationError', language)}</td>
                    <td>{t('statusValidationErrorAction', language)}</td>
                  </tr>
                  <tr>
                    <td><span className="status-code warning">429</span></td>
                    <td>{t('statusRateLimitExceeded', language)}</td>
                    <td>{t('statusRateLimitExceededAction', language)}</td>
                  </tr>
                  <tr>
                    <td><span className="status-code error">500</span></td>
                    <td>{t('statusServerError', language)}</td>
                    <td>{t('statusServerErrorAction', language)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{t('errorResponseFormat', language)}</h3>
            <CodeBlock 
              code={`{
  "success": false,
  "error": {
    "code": "VALIDATION_ERROR",
    "message": "The amount field is required",
    "details": {
      "field": "amount",
      "rule": "required"
    }
  }
}`}
              language="json"
            />

            <h3>{t('commonErrorCodes', language)}</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('tableCode', language)}</th>
                    <th>{t('tableDescription', language)}</th>
                    <th>{t('tableSolution', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>INVALID_API_KEY</code></td>
                    <td>{t('errorInvalidApiKey', language)}</td>
                    <td>{t('errorInvalidApiKeySolution', language)}</td>
                  </tr>
                  <tr>
                    <td><code>VALIDATION_ERROR</code></td>
                    <td>{t('errorValidation', language)}</td>
                    <td>{t('errorValidationSolution', language)}</td>
                  </tr>
                  <tr>
                    <td><code>DUPLICATE_EVENT</code></td>
                    <td>{t('errorDuplicate', language)}</td>
                    <td>{t('errorDuplicateSolution', language)}</td>
                  </tr>
                  <tr>
                    <td><code>RATE_LIMIT_EXCEEDED</code></td>
                    <td>{t('errorRateLimit', language)}</td>
                    <td>{t('errorRateLimitSolution', language)}</td>
                  </tr>
                  <tr>
                    <td><code>INVALID_PROVIDER</code></td>
                    <td>{t('errorInvalidProvider', language)}</td>
                    <td>{t('errorInvalidProviderSolution', language)}</td>
                  </tr>
                  <tr>
                    <td><code>INVALID_CURRENCY</code></td>
                    <td>{t('errorInvalidCurrency', language)}</td>
                    <td>{t('errorInvalidCurrencySolution', language)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>{t('retryStrategy', language)}</h3>
            <p>{t('retryStrategyDesc', language)}</p>
            <CodeBlock 
              code={`import time
import requests

def send_event_with_retry(url, headers, data, max_retries=3):
    for attempt in range(max_retries):
        try:
            response = requests.post(url, headers=headers, json=data, timeout=10)
            
            if response.status_code == 200:
                return response.json()
            
            # Retry on 5xx errors
            if response.status_code >= 500:
                wait_time = (2 ** attempt) * 1  # 1s, 2s, 4s
                time.sleep(wait_time)
                continue
            
            # Don't retry on client errors (4xx)
            return response.json()
            
        except requests.exceptions.Timeout:
            if attempt < max_retries - 1:
                time.sleep(2 ** attempt)
                continue
            raise
    
    raise Exception("Max retries exceeded")`}
              language="python"
            />
          </section>

          {/* Best Practices */}
          <section id="best-practices" className="section">
            <h2>{t('bestPracticesTitle', language)}</h2>

            <div className="best-practice-card">
              <div className="bp-header">
                <div className="bp-number">1</div>
                <h3>{t('bp1Title', language)}</h3>
              </div>
              <p>{t('bp1Desc', language)}</p>
              <CodeBlock 
                code={`Idempotency-Key: {event_type}-{payment_intent_id}-{timestamp}

${t('bp1CodeComment', language)}`}
                language="bash"
              />
              <div className="bp-why">
                <strong>{t('why', language)}</strong> {t('bp1Why', language)}
              </div>
            </div>

            <div className="best-practice-card">
              <div className="bp-header">
                <div className="bp-number">2</div>
                <h3>{t('bp2Title', language)}</h3>
              </div>
              <p>{t('bp2Desc', language)}</p>
              <div className="event-order">
                <span>1. initialized</span>
                <ArrowRight size={16} />
                <span>2. provider_requested</span>
                <ArrowRight size={16} />
                <span>3. provider_success</span>
                <ArrowRight size={16} />
                <span>4. order_created</span>
                <ArrowRight size={16} />
                <span>5. revenue_recorded</span>
              </div>
              <div className="bp-why">
                <strong>{t('why', language)}</strong> {t('bp2Why', language)}
              </div>
            </div>

            <div className="best-practice-card">
              <div className="bp-header">
                <div className="bp-number">3</div>
                <h3>{t('bp3Title', language)}</h3>
              </div>
              <p>{t('bp3Desc', language)}</p>
              <CodeBlock 
                code={`{
  "timestamp": "2026-01-13T10:30:00Z"
}`}
                language="json"
              />
              <div className="bp-why">
                <strong>{t('why', language)}</strong> {t('bp3Why', language)}
              </div>
            </div>

            <div className="best-practice-card">
              <div className="bp-header">
                <div className="bp-number">4</div>
                <h3>{t('bp4Title', language)}</h3>
              </div>
              <p>{t('bp4Desc', language)}</p>
              <CodeBlock 
                code={`initialized: { "amount": 99.99 }
provider_success: { "amount": 99.99 }
revenue_recorded: { "revenue_amount": 99.99 }`}
                language="json"
              />
              <div className="bp-why">
                <strong>{t('why', language)}</strong> {t('bp4Why', language)}
              </div>
            </div>

            <div className="best-practice-card">
              <div className="bp-header">
                <div className="bp-number">5</div>
                <h3>{t('bp5Title', language)}</h3>
              </div>
              <p>{t('bp5Desc', language)}</p>
              <CodeBlock 
                code={`fetch(url, {
  ...options,
  signal: AbortSignal.timeout(10000) // 10 seconds
})`}
                language="javascript"
              />
            </div>

            <div className="best-practice-card">
              <div className="bp-header">
                <div className="bp-number">6</div>
                <h3>{t('bp6Title', language)}</h3>
              </div>
              <p>{t('bp6Desc', language)}</p>
              <CodeBlock 
                code={`{
  "metadata": {
    "server_id": "web-01",
    "request_id": "req_abc123",
    "user_agent": "Mozilla/5.0...",
    "source_ip": "192.168.1.1",
    "processing_time_ms": 234
  }
}`}
                language="json"
              />
            </div>
          </section>

          {/* Testing */}
          <section id="testing" className="section">
            <h2>{t('testingTitle', language)}</h2>

            <h3>{t('dashboardCheckTitle', language)}</h3>
            <p>{t('dashboardCheckDesc', language)}</p>
            <div className="step-path">
              Dashboard <ArrowRight size={16} /> Payments <ArrowRight size={16} /> Search "order_12345"
            </div>
          </section>

          {/* Example Scenarios */}
          <section id="examples" className="section">
            <h2>{t('examplesTitle', language)}</h2>
            <p className="section-intro fire-forget-note">
              ⚡ {t('examplesNote', language)}
            </p>

            <h3>{t('example1Title', language)}</h3>
            <CodeBlock 
              code={`// SDK Client oluştur (uygulama başlangıcında bir kez)
const paymentSafety = new PaymentSafetyClient('sl_your_api_key');

// ========== CHECKOUT SAYFASINDA ==========

// 1. Payment Initialized (Fire & Forget - kullanıcı beklemez!)
paymentSafety.paymentInitialized({
    payment_intent_id: 'order_12345',
    amount: 99.99,
    currency: 'USD',
    provider: 'stripe',
    customer_email: 'john@example.com'
});

// ========== STRIPE'A ÖDEME İSTEĞİ ==========

// 2. Provider Requested (Fire & Forget)
paymentSafety.providerRequested({
    payment_intent_id: 'order_12345',
    provider: 'stripe',
    provider_request_id: 'pi_3ABC123',
    amount: 99.99,
    currency: 'USD'
});

// Stripe API çağrısı
const paymentIntent = await stripe.paymentIntents.create({...});

// ========== STRIPE BAŞARILI OLDU ==========

// 3. Provider Success (Fire & Forget)
paymentSafety.providerSuccess({
    payment_intent_id: 'order_12345',
    provider: 'stripe',
    provider_payment_id: paymentIntent.id,  // ← ZORUNLU! Webhook eşleştirmesi için
    provider_transaction_id: paymentIntent.latest_charge,
    amount: 99.99,
    currency: 'USD',
    provider_status: 'succeeded'
});

// ========== SİPARİŞ OLUŞTURULDU ==========

// 4. Order Created (Fire & Forget)
const order = await Order.create({...});  // Kendi DB işleminiz

paymentSafety.orderCreated({
    payment_intent_id: 'order_12345',
    order_id: order.id,
    amount: 99.99,
    currency: 'USD'
});

// Kullanıcıya başarı mesajı göster
return { success: true, orderId: order.id };`}
              language="javascript"
            />

            <h3>{t('example2Title', language)}</h3>
            <CodeBlock 
              code={`// 1. Payment Initialized
paymentSafety.paymentInitialized({
    payment_intent_id: 'order_12346',
    amount: 149.99,
    currency: 'USD',
    provider: 'stripe',
    customer_email: 'jane@example.com'
});

// 2. Provider Requested
paymentSafety.providerRequested({
    payment_intent_id: 'order_12346',
    provider: 'stripe',
    provider_request_id: 'pi_3DEF456',
    amount: 149.99,
    currency: 'USD'
});

try {
    // Stripe API çağrısı
    await stripe.paymentIntents.create({...});
} catch (stripeError) {
    // 3. Provider Failed (Fire & Forget)
    paymentSafety.providerFailed({
        payment_intent_id: 'order_12346',
        provider: 'stripe',
        error_code: stripeError.code,
        error_message: stripeError.message
    });
    
    // Kullanıcıya hata mesajı göster
    return { success: false, error: stripeError.message };
}`}
              language="javascript"
            />
          </section>

          {/* Webhook Integration */}
          <section id="webhook-integration" className="section">
            <h2>{t('webhookTitle', language)}</h2>

            <div className="warning-box critical">
              <AlertTriangle size={20} />
              <div>
                <strong>{t('critical', language)}:</strong> {t('webhookCritical', language)}
              </div>
            </div>

            <p>{t('webhookDesc', language)}</p>

            <CodeBlock 
              code={`Organization 1: https://api.silentloss.io/api/v1/webhooks/stripe/1
Organization 2: https://api.silentloss.io/api/v1/webhooks/stripe/2
Organization 3: https://api.silentloss.io/api/v1/webhooks/stripe/3
...
Organization 100: https://api.silentloss.io/api/v1/webhooks/stripe/100`}
              language="bash"
            />

            <h3>{t('webhookUrlFormat', language)}</h3>
            <CodeBlock 
              code={`https://api.silentloss.io/api/v1/webhooks/{provider}/{organization_id}
                                                              ↑
                                                    ${language === 'tr' ? 'Her müşteri için farklı!' : 'Different for each customer!'}`}
              language="bash"
            />

            <h3>{t('securityLayersTitle', language)}</h3>
            <div className="security-grid">
              <div className="security-card">
                <Lock size={24} />
                <h4>{t('securityUrlIsolation', language)}</h4>
                <p>{t('securityUrlIsolationDesc', language)}</p>
              </div>
              <div className="security-card">
                <Shield size={24} />
                <h4>{t('securityWebhookSecret', language)}</h4>
                <p>{t('securityWebhookSecretDesc', language)}</p>
              </div>
              <div className="security-card">
                <CheckCircle size={24} />
                <h4>{t('securitySignatureValidation', language)}</h4>
                <p>{t('securitySignatureValidationDesc', language)}</p>
              </div>
            </div>

            <h3>{t('whyProviderPaymentId', language)}</h3>
            <p>{t('whyProviderPaymentIdDesc', language)}</p>
            
            <CodeBlock 
              code={`${t('webhookMatchingCode1', language)}     payment_intent_id = "order_12345"
${t('webhookMatchingCode2', language)}   payment_intent = "pi_3ABC123"

${t('webhookMatchingCode3', language)}`}
              language="bash"
            />

            <h3>{t('stripeWebhookSetupTitle', language)}</h3>
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>{t('stripeStep1Title', language)}</h4>
                <div className="step-path">
                  Stripe Dashboard <ArrowRight size={16} /> Webhooks <ArrowRight size={16} /> Add endpoint
                </div>
                <ul>
                  <li><strong>URL:</strong> <code>https://api.silentloss.io/api/v1/webhooks/stripe/1</code></li>
                  <li><strong>Events:</strong> <code>payment_intent.succeeded</code>, <code>payment_intent.payment_failed</code></li>
                  <li><strong>Copy Signing Secret:</strong> <code>whsec_abc123...</code></li>
                </ul>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>{t('stripeStep2Title', language)}</h4>
                <CodeBlock 
                  code={`POST /api/v1/settings/providers
Authorization: Bearer {YOUR_API_TOKEN}

{
  "provider": "stripe",
  "environment": "production",
  "webhook_secret": "whsec_abc123..."
}`}
                  language="bash"
                />
              </div>
            </div>
          </section>

          {/* SDK Setup */}
          <section id="sdk-setup" className="section">
            <h2>{t('sdkSetupTitle', language)}</h2>
            <p className="section-intro fire-forget-note">
              ⚡ {t('sdkSetupDesc', language)}
            </p>

            <h3>{t('sdkJsTitle', language)}</h3>
            <CodeBlock code={sdkExamplesNodeJS} language="javascript" />

            <h3>{t('sdkPhpTitle', language)}</h3>
            <CodeBlock code={sdkExamplesPHP} language="php" />
          </section>

          {/* SDKs (kept for backward compatibility) */}
          <section id="sdks" className="section" style={{display: 'none'}}>
            <h2>{t('sdksTitle', language)}</h2>
          </section>

          {/* FAQ Section */}
          <section id="faq" className="section">
            <h2>{t('faqTitle', language)}</h2>
            
            <div className="faq-list">
              <div className="faq-item">
                <h4>{t('faq1Q', language)}</h4>
                <p>{t('faq1A', language)}</p>
              </div>
              
              <div className="faq-item">
                <h4>{t('faq2Q', language)}</h4>
                <p>{t('faq2A', language)}</p>
              </div>
              
              <div className="faq-item">
                <h4>{t('faq3Q', language)}</h4>
                <p>{t('faq3A', language)}</p>
              </div>
              
              <div className="faq-item">
                <h4>{t('faq4Q', language)}</h4>
                <p>{t('faq4A', language)}</p>
              </div>
              
              <div className="faq-item">
                <h4>{t('faq5Q', language)}</h4>
                <p>{t('faq5A', language)}</p>
              </div>
              
              <div className="faq-item">
                <h4>{t('faq6Q', language)}</h4>
                <p>{t('faq6A', language)}</p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="docs-footer">
            <div className="footer-content">
              <div className="footer-section">
                <h4>{t('footerSupport', language)}</h4>
                <ul>
                  <li><a href="mailto:support@silentloss.io"><ExternalLink size={14} /> support@silentloss.io</a></li>
                  <li><a href="https://docs.silentloss.io"><ExternalLink size={14} /> API Reference</a></li>
                  <li><a href="https://github.com/silentloss/issues"><ExternalLink size={14} /> Issue Tracker</a></li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>{t('footerRateLimits', language)}</h4>
                <ul>
                  <li>Production: 1000 requests/minute</li>
                  <li>Batch: 50 requests/minute</li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>{t('footerSla', language)}</h4>
                <ul>
                  <li>{t('footerUptime', language)}: 99.9%</li>
                  <li>{t('footerResponseTime', language)}: &lt; 200ms (p95)</li>
                  <li>{t('footerSupportResponse', language)}: &lt; 24 hours</li>
                </ul>
              </div>
            </div>
            <div className="footer-bottom">
              <p>{t('footerCopyright', language)}</p>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
